/**
 * DEC-721: Cheesy welcome modal for first-time invite redemption.
 *
 * Fires ONCE when BetaAccessContext flips to granted via the `?invite=`
 * redeem path — NOT on cookie revisits, NOT on email-match logins via
 * /api/auth/login. This is the "moment of arrival" for a brand-new user
 * clicking their email invite for the first time.
 *
 * The modal is rendered globally from App.js so it appears on whatever
 * route the user lands on after redemption (typically /welcome). After
 * the user clicks Continue or Close, BetaAccessContext.dismissWelcome()
 * sets `deco_beta_welcomed=1` in localStorage so it never fires again
 * on this browser.
 *
 * Confetti is CSS-only (no library) — small set of absolutely-positioned
 * span dots that fall and fade. Performance-safe.
 */

import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useBetaAccess } from '../context/BetaAccessContext';
import DecoWordmark from './DecoWordmark';

const CONFETTI_COLORS = ['#1a365d', '#5DADE2', '#22c55e', '#f59e0b', '#ec4899'];
const CONFETTI_COUNT = 28;

const BetaWelcomeModal = () => {
  const { welcomeContext, dismissWelcome } = useBetaAccess();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const previousFocusRef = useRef(null);

  // DEC-721 follow-up: primary CTA chains directly into account setup.
  // Sets sessionStorage flags that LandingPage reads on mount to open
  // AuthModal pre-populated with the invitee's email. Then dismisses
  // the welcome modal and navigates to /welcome (LandingPage) — which
  // is where AuthModal lives. If the user is already on /welcome
  // (typical case after redeem), navigate is a no-op and the
  // sessionStorage-driven useEffect in LandingPage fires immediately.
  const handleCreateAccount = () => {
    // Stash the flags FIRST so LandingPage's listener picks them up
    // immediately when the event fires below.
    sessionStorage.setItem('openAuthModal', 'true');
    if (welcomeContext?.inviteeEmail) {
      sessionStorage.setItem('signupEmail', welcomeContext.inviteeEmail);
    }
    // DEC-721 follow-up: pre-minted ownership token from the redeem
    // response. Lets AuthModal skip the verify-ownership OTP step
    // entirely — clicking the invite link IS the email-ownership proof.
    if (welcomeContext?.ownershipToken) {
      sessionStorage.setItem('signupOwnershipToken', welcomeContext.ownershipToken);
    }
    dismissWelcome();
    // The user is typically ALREADY on /welcome by the time the welcome
    // modal renders (PrivateRoute on / redirected them there post-redeem).
    // navigate('/welcome') in that case is a no-op — LandingPage doesn't
    // remount, so its mount-only sessionStorage-read useEffect never fires
    // again. Dispatch a custom event LandingPage explicitly listens for,
    // so it re-checks sessionStorage and opens AuthModal.
    window.dispatchEvent(new Event('deco:open-auth-modal'));
    // Still call navigate as a safety net for the rare case the user
    // isn't on /welcome yet (e.g., they redeemed while on /something-else).
    navigate('/welcome');
  };

  // Focus management — capture the previously focused element so we can
  // restore focus on close (WCAG 2.4.3 Focus Order).
  useEffect(() => {
    if (welcomeContext) {
      previousFocusRef.current = document.activeElement;
      // Defer focus to next tick so the portal has mounted.
      requestAnimationFrame(() => cardRef.current?.focus());
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus?.();
    }
  }, [welcomeContext]);

  // ESC dismisses, matching the rest of our modal patterns.
  useEffect(() => {
    if (!welcomeContext) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') dismissWelcome();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [welcomeContext, dismissWelcome]);

  if (!welcomeContext) return null;

  const inviterFirstName = welcomeContext.inviterFirstName;

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="beta-welcome-heading"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10050, // above AuthModal (10000) for the case it ever co-renders
        animation: 'betaWelcomeBackdropIn 0.2s ease',
      }}
      onClick={dismissWelcome}
    >
      {/* Confetti — absolutely positioned, doesn't affect layout. */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: CONFETTI_COUNT }).map((_, i) => {
          // nosemgrep: ajinabraham.njsscan.crypto.crypto_node.node_insecure_random_generator -- cosmetic confetti jitter (position/timing/drift); not security-sensitive.
          const jitter = () => Math.random();
          const left = (i * 100 / CONFETTI_COUNT) + (jitter() * 4 - 2);
          const delay = (jitter() * 0.6).toFixed(2);
          const duration = (1.6 + jitter() * 1.4).toFixed(2);
          const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
          const drift = Math.round(jitter() * 80 - 40);
          return (
            <span
              key={i}
              style={{
                position: 'absolute',
                top: '-10px',
                left: `${left}%`,
                width: '8px',
                height: '12px',
                background: color,
                borderRadius: '2px',
                transform: 'rotate(0deg)',
                animation: `betaWelcomeConfetti ${duration}s ${delay}s ease-out forwards`,
                ['--drift']: `${drift}px`,
              }}
            />
          );
        })}
      </div>

      <div
        ref={cardRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: 'white',
          maxWidth: '480px',
          width: 'calc(100% - 48px)',
          padding: '40px 32px 32px',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          textAlign: 'center',
          animation: 'betaWelcomeCardIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Close X — top-right, accessible */}
        <button
          type="button"
          onClick={dismissWelcome}
          aria-label="Close welcome message"
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '24px',
            color: '#94a3b8',
            cursor: 'pointer',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#1a365d'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
        >
          ×
        </button>

        {/* DECO wordmark — brand-locked via shared component. Previously
            this hard-coded letterSpacing: '12px' (off-spec). Audit
            2026-05-13 brought it back to the 16px brand spec via the
            `size="md"` preset. */}
        <DecoWordmark size="md" block style={{ marginBottom: '24px' }} />

        <h1
          id="beta-welcome-heading"
          style={{
            fontSize: '24px',
            fontWeight: 600,
            color: 'var(--navy)',
            margin: '0 0 12px 0',
          }}
        >
          You're in! 🎉
        </h1>

        <p style={{
          fontSize: '15px',
          color: '#4a5568',
          lineHeight: 1.6,
          margin: '0 0 28px 0',
        }}>
          {inviterFirstName ? (
            <>
              <strong style={{ color: 'var(--navy)' }}>{inviterFirstName}</strong>{' '}
              invited you to DECO. Welcome — let's get you started.
            </>
          ) : (
            <>Welcome to DECO. Let's get you started.</>
          )}
        </p>

        <button
          type="button"
          onClick={handleCreateAccount}
          style={{
            width: '100%',
            padding: '14px 24px',
            fontSize: '15px',
            fontWeight: 600,
            color: 'white',
            background: 'var(--navy)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'background 0.2s ease',
            minHeight: '44px',
            fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-action-primary-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--navy)'; }}
        >
          Create your account →
        </button>
      </div>

      {/* Animations — scoped via unique keyframe names so they don't
          collide with anything else. */}
      <style>{`
        @keyframes betaWelcomeBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes betaWelcomeCardIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes betaWelcomeConfetti {
          0% {
            transform: translateY(0) translateX(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) translateX(var(--drift, 0)) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default BetaWelcomeModal;
